export interface HelpField {
	name: string;
	type: string;
	example: string;
	description: string;
	required?: boolean;
}

export interface HelpNodeContent {
	id: string;
	name: string;
	icon: string;
	category: 'flow-control' | 'processing' | 'integrations' | 'advanced' | 'triggers';
	description: string;
	whenToUse: string[];
	configurationOptions: HelpField[];
	inputs: string[];
	outputs: {
		description: string;
		structure: string;
	};
	runtimeBehavior: string;
	errorsFailures: string[];
	example: {
		title: string;
		description: string;
		config?: Record<string, any>;
	};
}

export const HELP_CONTENT: HelpNodeContent[] = [
	// Trigger Nodes (New Start Node Types)
	{
		id: 'start.form',
		name: 'Form Trigger',
		icon: '📝',
		category: 'triggers',
		description: 'Creates a user-facing form that triggers the workflow when submitted',
		whenToUse: [
			'Collect user input to start a workflow',
			'Create submission forms for external users',
			'Trigger workflows based on form data',
			'Replace manual workflow execution with user forms',
		],
		configurationOptions: [
			{
				name: 'formSchema',
				type: 'object',
				example: '{ "type": "object", "properties": { "name": { "type": "string" } } }',
				description: 'JSON Schema defining the form fields and validation',
				required: true,
			},
			{
				name: 'metadata.title',
				type: 'string',
				example: 'Request Support',
				description: 'Title displayed at the top of the form',
			},
			{
				name: 'metadata.description',
				type: 'string',
				example: 'Please fill out this form to request support',
				description: 'Description text shown below the title',
			},
			{
				name: 'styling.theme',
				type: 'string',
				example: 'modern',
				description: 'Visual theme: default, minimal, modern, or classic',
			},
			{
				name: 'validation.allowDuplicates',
				type: 'boolean',
				example: 'true',
				description: 'Whether to allow multiple submissions from the same user',
			},
		],
		inputs: ['No inputs - this is a workflow trigger'],
		outputs: {
			description: 'Form submission data is available to downstream nodes',
			structure:
				'{ "formData": { "name": "John Doe", "email": "john@example.com" }, "submittedAt": "2024-01-15T10:00:00Z", "submittedBy": "user123" }',
		},
		runtimeBehavior: 'Triggered by form submission - creates workflow execution with form data',
		errorsFailures: [
			'Form validation errors prevent submission',
			'Invalid form schema causes configuration errors',
			'Session timeout if form is left open too long',
		],
		example: {
			title: 'Support Request Form',
			description:
				'Create a form for users to submit support requests that automatically creates tickets',
			config: {
				formSchema: {
					type: 'object',
					properties: {
						name: { type: 'string', title: 'Your Name' },
						email: { type: 'string', format: 'email', title: 'Email Address' },
						issue: { type: 'string', title: 'Describe your issue' },
					},
					required: ['name', 'email', 'issue'],
				},
				metadata: {
					title: 'Support Request',
					description: 'Tell us how we can help you',
					successMessage: "Thank you! We'll get back to you soon.",
				},
			},
		},
	},
	{
		id: 'start.webhook',
		name: 'Webhook Trigger',
		icon: '🔗',
		category: 'triggers',
		description:
			'Creates a webhook endpoint that external systems can call to trigger workflows',
		whenToUse: [
			'Integrate with third-party services (GitHub, Stripe, etc.)',
			'Receive real-time notifications from external systems',
			'Trigger workflows from other applications',
			'Create custom API endpoints for workflow execution',
		],
		configurationOptions: [
			{
				name: 'path',
				type: 'string',
				example: 'github-push',
				description: 'URL path for the webhook endpoint',
				required: true,
			},
			{
				name: 'authentication',
				type: 'string',
				example: 'signature',
				description: 'Authentication method: none, signature, or token',
			},
			{
				name: 'secretKey',
				type: 'string',
				example: 'your-secret-key-here',
				description: 'Secret key for signature or token authentication',
			},
			{
				name: 'expectedPayload',
				type: 'object',
				example: '{ "type": "object", "properties": { "event": { "type": "string" } } }',
				description: 'JSON Schema to validate incoming webhook payloads',
			},
			{
				name: 'headers',
				type: 'object',
				example: '{ "X-GitHub-Event": "push" }',
				description: 'Expected HTTP headers for validation',
			},
		],
		inputs: ['No inputs - this is a webhook trigger'],
		outputs: {
			description: 'Webhook payload data is available to downstream nodes',
			structure:
				'{ "webhookData": { "event": "push", "repository": "my-repo" }, "headers": { "X-GitHub-Event": "push" }, "receivedAt": "2024-01-15T10:00:00Z" }',
		},
		runtimeBehavior:
			'Triggered by HTTP POST requests - validates payload and creates workflow execution',
		errorsFailures: [
			'Authentication failures (invalid signature/token)',
			'Payload validation errors',
			'Missing required headers',
			'Webhook endpoint not found',
		],
		example: {
			title: 'GitHub Push Webhook',
			description: 'Trigger deployments when code is pushed to GitHub',
			config: {
				path: 'github-deploy',
				authentication: 'signature',
				secretKey: 'your-github-webhook-secret',
				headers: {
					'X-GitHub-Event': 'push',
				},
				expectedPayload: {
					type: 'object',
					properties: {
						ref: { type: 'string' },
						repository: { type: 'object' },
					},
					required: ['ref', 'repository'],
				},
			},
		},
	},
	{
		id: 'start.schedule',
		name: 'Schedule Trigger',
		icon: '⏰',
		category: 'triggers',
		description:
			'Executes workflows automatically on a time-based schedule using cron expressions',
		whenToUse: [
			'Run workflows at regular intervals',
			'Automate daily, weekly, or monthly tasks',
			'Generate periodic reports',
			'Perform maintenance or cleanup tasks',
		],
		configurationOptions: [
			{
				name: 'cronExpression',
				type: 'string',
				example: '0 9 * * 1-5',
				description: 'Cron expression defining when to run (minute hour day month weekday)',
				required: true,
			},
			{
				name: 'timezone',
				type: 'string',
				example: 'America/New_York',
				description: 'Timezone for schedule execution (IANA timezone identifier)',
			},
			{
				name: 'enabled',
				type: 'boolean',
				example: 'true',
				description: 'Whether the schedule is currently active',
			},
			{
				name: 'maxExecutions',
				type: 'number',
				example: '100',
				description: 'Maximum number of times to execute (optional limit)',
			},
			{
				name: 'payload',
				type: 'object',
				example: '{ "source": "scheduled", "type": "daily-report" }',
				description: 'Static data to include with each scheduled execution',
			},
			{
				name: 'retryPolicy.maxRetries',
				type: 'number',
				example: '3',
				description: 'Number of retry attempts if execution fails',
			},
		],
		inputs: ['No inputs - this is a scheduled trigger'],
		outputs: {
			description:
				'Schedule information and optional payload data available to downstream nodes',
			structure:
				'{ "scheduledAt": "2024-01-15T09:00:00Z", "executionCount": 42, "payload": { "source": "scheduled" } }',
		},
		runtimeBehavior: 'Automatically triggered by the scheduler based on cron expression',
		errorsFailures: [
			'Invalid cron expression format',
			'Timezone not recognized',
			'Execution limit reached (maxExecutions)',
			'Schedule disabled or past end date',
		],
		example: {
			title: 'Daily Report Generator',
			description: 'Generate and email daily reports every weekday at 9 AM',
			config: {
				cronExpression: '0 9 * * 1-5',
				timezone: 'America/New_York',
				enabled: true,
				payload: {
					reportType: 'daily',
					department: 'sales',
				},
				metadata: {
					title: 'Daily Sales Report',
					description: 'Automated daily sales report generation',
				},
			},
		},
	},

	// Flow Control Nodes
	{
		id: 'start',
		name: 'Start Node (Legacy)',
		icon: '▶️',
		category: 'flow-control',
		description:
			'[DEPRECATED] Legacy workflow trigger - use Form, Webhook, or Schedule triggers instead',
		whenToUse: [
			'Legacy workflows (consider migrating to new trigger types)',
			'Manual workflow execution during development',
			'Simple workflows without external triggers',
		],
		configurationOptions: [],
		inputs: ['No inputs required - this is the workflow entry point'],
		outputs: {
			description: 'No specific outputs, but triggers the workflow execution',
			structure: 'workflow.startedAt = "2024-01-15T10:00:00Z"',
		},
		runtimeBehavior: 'Synchronous - immediately triggers connected downstream nodes',
		errorsFailures: [
			'Cannot fail - always succeeds',
			'Must be connected to at least one downstream node',
		],
		example: {
			title: 'Basic Workflow Start',
			description:
				'Legacy start node - consider using Form, Webhook, or Schedule triggers for new workflows',
		},
	},
	{
		id: 'end',
		name: 'End Node',
		icon: '⏹️',
		category: 'flow-control',
		description: 'Marks the completion of workflow execution',
		whenToUse: [
			'Define workflow completion points',
			'Ensure proper workflow termination',
			'Mark successful execution paths',
		],
		configurationOptions: [],
		inputs: ['Receives data from any upstream node'],
		outputs: {
			description: 'Finalizes workflow execution and logs completion',
			structure: 'workflow.completedAt = "2024-01-15T10:05:00Z"',
		},
		runtimeBehavior: 'Synchronous - marks workflow as complete when reached',
		errorsFailures: [
			'Cannot fail - always succeeds',
			'Must have at least one incoming connection',
		],
		example: {
			title: 'Workflow Completion',
			description: 'Place End nodes at logical completion points in your workflow',
		},
	},

	// Processing Nodes
	{
		id: 'api-call',
		name: 'API Call Node',
		icon: '🌐',
		category: 'processing',
		description: 'Makes HTTP requests to external APIs and processes responses',
		whenToUse: [
			'Integrate with third-party services',
			'Fetch data from REST APIs',
			'Send data to external systems',
			'Validate information with external services',
		],
		configurationOptions: [
			{
				name: 'url',
				type: 'string',
				example: 'https://api.example.com/users/{{userId}}',
				description: 'API endpoint URL with token support',
				required: true,
			},
			{
				name: 'method',
				type: 'select',
				example: 'GET',
				description: 'HTTP method (GET, POST, PUT, DELETE)',
				required: true,
			},
			{
				name: 'headers',
				type: 'object',
				example: '{"Authorization": "Bearer {{token}}"}',
				description: 'HTTP headers for the request',
			},
			{
				name: 'requestSchema',
				type: 'object',
				example: '{"userId": "string"}',
				description: 'Expected request payload structure',
			},
			{
				name: 'responseSchema',
				type: 'object',
				example: '{"user": "object"}',
				description: 'Expected response structure',
				required: true,
			},
		],
		inputs: ['Any upstream data available as {{tokens}} in URL, headers, and payload'],
		outputs: {
			description: 'API response data and metadata',
			structure: `context.data.api = {
  response: { user: { id: 123, name: "John" } },
  status: 200,
  headers: { "content-type": "application/json" }
}`,
		},
		runtimeBehavior: 'Asynchronous - waits for API response before continuing workflow',
		errorsFailures: [
			'Network timeouts or connection failures',
			'HTTP error status codes (4xx, 5xx)',
			'Invalid response format',
			'Missing required configuration',
		],
		example: {
			title: 'User Profile Fetch',
			description: 'Fetch user details from an external API using form submission data',
			config: {
				url: 'https://api.example.com/users/{{userId}}',
				method: 'GET',
				headers: { Authorization: 'Bearer {{apiToken}}' },
			},
		},
	},
	{
		id: 'transform',
		name: 'Transform Node',
		icon: '🔄',
		category: 'processing',
		description: 'Transforms and reshapes data using expressions and mappings',
		whenToUse: [
			'Reshape data for downstream nodes',
			'Calculate derived values',
			'Format data for specific requirements',
			'Combine multiple fields into new structures',
		],
		configurationOptions: [
			{
				name: 'mode',
				type: 'select',
				example: 'basic',
				description: 'Basic (field mapping) or Advanced (JSON expressions)',
				required: true,
			},
			{
				name: 'mappings',
				type: 'object',
				example: '{"fullName": "{{firstName}} {{lastName}}"}',
				description: 'Field mappings with expressions',
				required: true,
			},
		],
		inputs: ['Any upstream data accessible via {{tokens}} in expressions'],
		outputs: {
			description: 'Transformed data with new field structure',
			structure: `context.data.transform = {
  fullName: "John Doe",
  email: "john.doe@example.com",
  calculatedField: 42
}`,
		},
		runtimeBehavior: 'Synchronous - immediately processes data transformation',
		errorsFailures: [
			'Invalid expression syntax',
			'Missing referenced fields',
			'Type conversion errors',
		],
		example: {
			title: 'User Profile Transformation',
			description: 'Combine first and last name, calculate age from birthdate',
			config: {
				mode: 'basic',
				mappings: {
					fullName: '{{firstName}} {{lastName}}',
					age: '{{calculateAge(birthDate)}}',
				},
			},
		},
	},
	{
		id: 'condition',
		name: 'Condition Node',
		icon: '🔀',
		category: 'processing',
		description: 'Evaluates conditions and creates branching logic with clear true/false paths',
		whenToUse: [
			'Create branching workflows based on data values',
			'Validate data before processing',
			'Implement business logic rules',
			'Route users through different workflow paths',
			'Filter and process data conditionally',
		],
		configurationOptions: [
			{
				name: 'match',
				type: 'select',
				example: 'all',
				description: 'Match ALL conditions (AND) or ANY condition (OR)',
				required: true,
			},
			{
				name: 'conditions',
				type: 'array',
				example: '[{"field": "age", "operator": ">=", "value": "18"}]',
				description: 'List of conditions to evaluate',
				required: true,
			},
		],
		inputs: ['Upstream data fields for condition evaluation'],
		outputs: {
			description:
				'Two distinct paths: Green (✓ True) path when conditions pass, Red (✕ False) path when they fail',
			structure: `context.data.condition = {
  result: true,
  evaluatedData: { age: 25 },
  matchType: "all"
}`,
		},
		runtimeBehavior:
			'Synchronous - evaluates conditions and routes to appropriate colored path (green for true, red for false)',
		errorsFailures: [
			'Invalid condition expressions',
			'Missing referenced fields',
			'Type comparison errors',
		],
		example: {
			title: 'Age Verification',
			description: 'Check if user is over 18 and has verified email',
			config: {
				match: 'all',
				conditions: [
					{ field: 'age', operator: '>=', value: '18' },
					{ field: 'emailVerified', operator: '=', value: 'true' },
				],
			},
		},
	},

	// Integration Nodes
	{
		id: 'database',
		name: 'Database Node',
		icon: '🗄️',
		category: 'integrations',
		description: 'Executes SQL queries against databases with dynamic parameters',
		whenToUse: [
			'Query user data from databases',
			'Store workflow results',
			'Validate data against existing records',
			'Fetch configuration or lookup data',
		],
		configurationOptions: [
			{
				name: 'connection.host',
				type: 'string',
				example: 'localhost',
				description: 'Database server hostname',
				required: true,
			},
			{
				name: 'connection.database',
				type: 'string',
				example: 'myapp_db',
				description: 'Database name',
				required: true,
			},
			{
				name: 'connection.username',
				type: 'string',
				example: 'db_user',
				description: 'Database username',
				required: true,
			},
			{
				name: 'connection.password',
				type: 'string',
				example: '{{db_password}}',
				description: 'Database password (supports tokens)',
				required: true,
			},
			{
				name: 'query',
				type: 'text',
				example: 'SELECT * FROM users WHERE id = :userId',
				description: 'SQL query with parameters',
				required: true,
			},
			{
				name: 'parameters',
				type: 'object',
				example: '{"userId": "{{userId}}"}',
				description: 'Query parameters with token values',
			},
		],
		inputs: ['Token values for query parameters and connection details'],
		outputs: {
			description: 'Query results and metadata',
			structure: `context.data.database = {
  rows: [{ id: 123, name: "John", email: "john@example.com" }],
  first: { id: 123, name: "John", email: "john@example.com" },
  rowCount: 1
}`,
		},
		runtimeBehavior: 'Asynchronous - connects to database, executes query, returns results',
		errorsFailures: [
			'Database connection failures',
			'Invalid SQL syntax',
			'Permission denied errors',
			'Parameter resolution failures',
		],
		example: {
			title: 'User Profile Query',
			description: 'Fetch user details by ID from form submission',
			config: {
				query: 'SELECT id, name, email, created_at FROM users WHERE id = :userId',
				parameters: { userId: '{{formData.userId}}' },
			},
		},
	},
	{
		id: 'email',
		name: 'Email Node',
		icon: '📧',
		category: 'integrations',
		description: 'Sends templated emails with dynamic content from upstream data',
		whenToUse: [
			'Send notifications to users',
			'Deliver workflow results via email',
			'Send confirmation or receipt emails',
			'Alert administrators of important events',
		],
		configurationOptions: [
			{
				name: 'from.address',
				type: 'string',
				example: 'noreply@vquip.com',
				description: 'Sender email address (currently only noreply@vquip.com is supported)',
				required: true,
			},
			{
				name: 'to',
				type: 'array',
				example: '["{{userEmail}}"]',
				description: 'Recipient email addresses',
				required: true,
			},
			{
				name: 'subject',
				type: 'string',
				example: 'Welcome {{firstName}}!',
				description: 'Email subject with token support',
				required: true,
			},
			{
				name: 'template',
				type: 'text',
				example: 'Hello {{firstName}}, welcome!',
				description: 'Email body template (or use htmlContent for custom HTML)',
				required: false,
			},
			{
				name: 'htmlContent',
				type: 'text',
				example: '<h1>Hello {{firstName}}</h1><p>Welcome!</p>',
				description: 'Custom HTML email content (alternative to template)',
				required: false,
			},
			{
				name: 'templateFormat',
				type: 'select',
				example: 'html',
				description: 'Email format (html or plaintext)',
			},
		],
		inputs: ['User data for email personalization and recipient information'],
		outputs: {
			description: 'Email sending results and metadata',
			structure: `context.data.email = {
  message_id: "abc-123",
  email_sent: true,
  recipients: ["user@example.com"],
  sent_at: "2024-01-15T10:00:00Z"
}`,
		},
		runtimeBehavior: 'Asynchronous - sends email and waits for delivery confirmation',
		errorsFailures: [
			'Invalid email addresses',
			'Email service connection failures',
			'Template rendering errors',
			'Attachment processing failures',
		],
		example: {
			title: 'Welcome Email',
			description: 'Send personalized welcome email to new user registration',
			config: {
				to: ['{{userEmail}}'],
				subject: 'Welcome to {{companyName}}, {{firstName}}!',
				template: 'Hello {{firstName}}, thank you for joining!',
			},
		},
	},
	{
		id: 'slack',
		name: 'Slack Node',
		icon: '💬',
		category: 'integrations',
		description: 'Sends messages to Slack channels with dynamic content',
		whenToUse: [
			'Notify teams of workflow events',
			'Send alerts to Slack channels',
			'Share workflow results with team',
			'Request approvals via Slack',
		],
		configurationOptions: [
			{
				name: 'botToken',
				type: 'string',
				example: '{{slack_bot_token}}',
				description: 'Slack Bot Token for authentication',
				required: true,
			},
			{
				name: 'channel',
				type: 'string',
				example: '#notifications',
				description: 'Slack channel or user ID',
				required: true,
			},
			{
				name: 'message',
				type: 'text',
				example: 'New user registered: {{userName}}',
				description: 'Message text with token support',
				required: true,
			},
			{
				name: 'threadTs',
				type: 'string',
				example: '{{original_message_ts}}',
				description: 'Thread timestamp for replies',
			},
		],
		inputs: ['User data and context for message personalization'],
		outputs: {
			description: 'Slack message sending results',
			structure: `context.data.slack = {
  message_sent: true,
  channel_id: "C1234567890",
  message_ts: "1705312800.123456",
  message_url: "https://workspace.slack.com/..."
}`,
		},
		runtimeBehavior: 'Asynchronous - sends message to Slack and waits for confirmation',
		errorsFailures: [
			'Invalid Slack bot token',
			'Channel not found or access denied',
			'Message formatting errors',
			'Rate limiting by Slack API',
		],
		example: {
			title: 'New User Notification',
			description: 'Notify team channel when new user completes registration',
			config: {
				channel: '#user-notifications',
				message: '🎉 New user registered: {{userName}} ({{userEmail}})',
			},
		},
	},
	{
		id: 'hubspot',
		name: 'HubSpot Node',
		icon: '🏢',
		category: 'integrations',
		description: 'Creates or updates HubSpot CRM records with dynamic field mapping',
		whenToUse: [
			'Sync form submissions to HubSpot',
			'Update contact information',
			'Create new companies or deals',
			'Track lead interactions',
		],
		configurationOptions: [
			{
				name: 'apiEndpoint',
				type: 'select',
				example: '/crm/v3/objects/contacts',
				description: 'HubSpot API endpoint',
				required: true,
			},
			{
				name: 'matchField',
				type: 'string',
				example: 'email',
				description: 'Field to match for updates vs creates',
				required: true,
			},
			{
				name: 'fieldMappings',
				type: 'object',
				example: '{"email": "{{userEmail}}"}',
				description: 'HubSpot field mappings',
				required: true,
			},
		],
		inputs: ['Form or user data to sync with HubSpot'],
		outputs: {
			description: 'HubSpot operation results and record data',
			structure: `context.data.hubspot = {
  hubspot_id: "12345",
  action: "created",
  hubspot_data: { email: "user@example.com" },
  created_at: "2024-01-15T10:00:00Z"
}`,
		},
		runtimeBehavior: 'Asynchronous - creates or updates HubSpot record based on match field',
		errorsFailures: [
			'Invalid HubSpot API credentials',
			'Missing required HubSpot fields',
			'Duplicate record conflicts',
			'API rate limiting',
		],
		example: {
			title: 'Contact Creation',
			description: 'Create new HubSpot contact from form submission data',
			config: {
				apiType: 'contacts',
				operation: 'create',
				properties: {
					email: '{{formData.email}}',
					firstname: '{{formData.firstName}}',
					lastname: '{{formData.lastName}}',
				},
			},
		},
	},
	{
		id: 'google-sheets',
		name: 'Google Sheets Node',
		icon: '📊',
		category: 'integrations',
		description: 'Writes data to Google Sheets spreadsheets with dynamic content and variable support',
		whenToUse: [
			'Log form submissions to spreadsheets',
			'Export workflow results to sheets',
			'Create dynamic reports and dashboards',
			'Share data with non-technical team members',
			'Track workflow metrics and KPIs',
		],
		configurationOptions: [
			{
				name: 'spreadsheetId',
				type: 'string',
				example: '1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms',
				description: 'Google Sheets spreadsheet ID (found in the URL)',
				required: true,
			},
			{
				name: 'range',
				type: 'string',
				example: 'Sheet1!A2',
				description: 'Range to write data (format: SheetName!CellReference)',
				required: true,
			},
			{
				name: 'values',
				type: 'array',
				example: '[["{{submission.name}}", "{{submission.email}}"]]',
				description: '2D array of values to write (each row is an array)',
				required: true,
			},
			{
				name: 'append',
				type: 'boolean',
				example: 'true',
				description: 'Append to next available row (true) or overwrite existing range (false)',
			},
		],
		inputs: ['Form submissions, API responses, or any upstream data for spreadsheet columns'],
		outputs: {
			description: 'Google Sheets write operation results and metadata',
			structure: `context.data.googleSheets = {
  spreadsheetId: "1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms",
  range: "Sheet1!A2:B2",
  rowsWritten: 1,
  columnsWritten: 2,
  appendMode: true,
  success: true,
  updatedRange: "Sheet1!A2:B2"
}`,
		},
		runtimeBehavior: 'Asynchronous - connects to Google Sheets API, writes data, and returns results',
		errorsFailures: [
			'Invalid spreadsheet ID or insufficient permissions',
			'Range format errors or non-existent sheets',
			'Google Sheets API rate limiting',
			'Authentication or authorization failures',
			'Network connectivity issues',
		],
		example: {
			title: 'Form Submission Logger',
			description: 'Log all form submissions to a Google Sheets tracking spreadsheet',
			config: {
				spreadsheetId: '1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms',
				range: 'Submissions!A2',
				values: [
					[
						'{{submission.name}}',
						'{{submission.email}}',
						'{{submission.phone}}',
						'{{now}}',
					],
				],
				append: true,
			},
		},
	},
	{
		id: 'submission-form',
		name: 'Submission Form Node',
		icon: '📝',
		category: 'integrations',
		description: 'Collects user input through dynamic forms with validation',
		whenToUse: [
			'Collect user information',
			'Create multi-step forms',
			'Gather workflow parameters',
			'Enable user interaction in workflows',
		],
		configurationOptions: [
			{
				name: 'fields',
				type: 'array',
				example: '[{"id": "email", "type": "email", "label": "Email"}]',
				description: 'Form field definitions',
				required: true,
			},
			{
				name: 'title',
				type: 'string',
				example: 'User Registration',
				description: 'Form title',
			},
			{
				name: 'description',
				type: 'string',
				example: 'Please fill out your details',
				description: 'Form description',
			},
		],
		inputs: ['No inputs - this is typically the first interactive node'],
		outputs: {
			description: 'User-submitted form data',
			structure: `context.data.form = {
  email: "user@example.com",
  firstName: "John",
  lastName: "Doe",
  submittedAt: "2024-01-15T10:00:00Z"
}`,
		},
		runtimeBehavior: 'Interactive - pauses workflow until user submits form',
		errorsFailures: [
			'Form validation errors',
			'Required field missing',
			'Invalid input format',
			'User abandons form',
		],
		example: {
			title: 'User Registration Form',
			description: 'Collect basic user information for account creation',
			config: {
				fields: [
					{ id: 'firstName', type: 'text', label: 'First Name', required: true },
					{ id: 'email', type: 'email', label: 'Email Address', required: true },
				],
			},
		},
	},

	// Advanced Nodes
	{
		id: 'loop',
		name: 'Loop Node',
		icon: '🔄',
		category: 'advanced',
		description: 'Iterates over arrays and executes child nodes for each item',
		whenToUse: [
			'Process multiple records from database',
			'Send emails to multiple recipients',
			'Validate multiple form entries',
			'Batch process API calls',
		],
		configurationOptions: [
			{
				name: 'arrayField',
				type: 'string',
				example: '{{users}}',
				description: 'Array field to iterate over',
				required: true,
			},
			{
				name: 'itemName',
				type: 'string',
				example: 'user',
				description: 'Variable name for current item (default: item)',
			},
		],
		inputs: ['Array data from upstream nodes to iterate over'],
		outputs: {
			description: 'Loop execution results and item data',
			structure: `context.data.loop = {
  results: [...], // Array of results from each iteration
  count: 5,
  lastItem: { id: 5, name: "Last User" }
}`,
		},
		runtimeBehavior:
			'Synchronous per iteration - executes child nodes sequentially for each array item',
		errorsFailures: [
			'Array field not found or not an array',
			'Child node execution failures',
			'Infinite loop protection',
		],
		example: {
			title: 'Multi-User Email',
			description: 'Send personalized emails to each user in database results',
			config: {
				arrayField: '{{database.rows}}',
				itemName: 'user',
			},
		},
	},
	{
		id: 'wait',
		name: 'Wait/Delay Node',
		icon: '⏰',
		category: 'advanced',
		description: 'Pauses workflow execution for specified time or until timestamp',
		whenToUse: [
			'Add delays between API calls',
			'Wait for external processes',
			'Schedule workflow continuation',
			'Implement rate limiting',
		],
		configurationOptions: [
			{
				name: 'delayMs',
				type: 'number',
				example: '5000',
				description: 'Delay duration in milliseconds',
			},
			{
				name: 'resumeAt',
				type: 'string',
				example: '2024-12-31T23:59:59Z',
				description: 'ISO timestamp to resume at',
			},
		],
		inputs: ['Optional timestamp data for dynamic scheduling'],
		outputs: {
			description: 'Wait completion metadata',
			structure: `context.data.wait = {
  startedAt: "2024-01-15T10:00:00Z",
  resumedAt: "2024-01-15T10:00:05Z",
  actualDuration: 5000
}`,
		},
		runtimeBehavior: 'Asynchronous - pauses workflow for specified duration',
		errorsFailures: [
			'Invalid timestamp format',
			'Negative delay values',
			'System scheduling errors',
		],
		example: {
			title: 'API Rate Limiting',
			description: 'Wait 2 seconds between API calls to respect rate limits',
			config: {
				delayMs: 2000,
			},
		},
	},
	{
		id: 'approval',
		name: 'Approval Node',
		icon: '✅',
		category: 'advanced',
		description: 'Pauses workflow until human approval or rejection is received',
		whenToUse: [
			'High-value transaction approvals',
			'Content review workflows',
			'Budget approval processes',
			'Quality assurance gates',
		],
		configurationOptions: [
			{
				name: 'approvers',
				type: 'array',
				example: '["admin@company.com"]',
				description: 'List of email addresses who can approve',
				required: true,
			},
			{
				name: 'message',
				type: 'text',
				example: 'Please approve this expense',
				description: 'Message shown to approvers',
				required: true,
			},
			{
				name: 'timeoutMs',
				type: 'number',
				example: '86400000',
				description: 'Timeout in milliseconds (optional)',
			},
		],
		inputs: ['Context data to include in approval request'],
		outputs: {
			description: 'Approval decision and metadata',
			structure: `context.data.approval = {
  status: "approved", // or "rejected"
  approvedBy: "admin@company.com",
  at: "2024-01-15T10:00:00Z",
  comments: "Looks good to proceed"
}`,
		},
		runtimeBehavior: 'Interactive - pauses workflow until approval decision is made',
		errorsFailures: [
			'Approval timeout',
			'Invalid approver email',
			'Notification delivery failures',
		],
		example: {
			title: 'Expense Approval',
			description: 'Require manager approval for expenses over $500',
			config: {
				approvers: ['manager@company.com'],
				message: 'Please approve expense of ${{amount}} for {{description}}',
			},
		},
	},
	{
		id: 'logger',
		name: 'Logger Node',
		icon: '📝',
		category: 'advanced',
		description: 'Debug and log workflow data at specified points for monitoring',
		whenToUse: [
			'Debug workflow execution',
			'Monitor data transformations',
			'Audit workflow behavior',
			'Track performance metrics',
		],
		configurationOptions: [
			{
				name: 'fields',
				type: 'array',
				example: '["{{userId}}", "{{email}}"]',
				description: 'Specific fields to log (empty = log all)',
			},
			{
				name: 'logLevel',
				type: 'select',
				example: 'info',
				description: 'Log level (debug, info, warn, error)',
			},
		],
		inputs: ['Any upstream data available for logging'],
		outputs: {
			description: 'Logging metadata (does not modify workflow data)',
			structure: `context.data.logger = {
  logged: true,
  loggedAt: "2024-01-15T10:00:00Z",
  logLevel: "info"
}`,
		},
		runtimeBehavior: 'Synchronous - immediately logs data and continues',
		errorsFailures: [
			'Logging service unavailable',
			'Invalid field references',
			'Rarely fails - designed for reliability',
		],
		example: {
			title: 'Debug Data Flow',
			description: 'Log user data after API call to verify correct transformation',
			config: {
				fields: ['{{api.response.user.id}}', '{{api.response.user.email}}'],
				logLevel: 'debug',
			},
		},
	},
	{
		id: 'validator',
		name: 'Validator Node',
		icon: '🛡️',
		category: 'advanced',
		description: 'Validates data against rules and optionally branches on failure',
		whenToUse: [
			'Validate API responses',
			'Check data quality',
			'Enforce business rules',
			'Input sanitization',
		],
		configurationOptions: [
			{
				name: 'rules',
				type: 'array',
				example: '[{"field": "email", "type": "isEmail"}]',
				description: 'Validation rules to apply',
				required: true,
			},
			{
				name: 'onFail',
				type: 'select',
				example: 'stop',
				description: 'Action on validation failure (stop, continue, branch)',
				required: true,
			},
		],
		inputs: ['Data to validate from upstream nodes'],
		outputs: {
			description: 'Validation results and error details',
			structure: `context.data.validator = {
  valid: false,
  errors: ["Invalid email format"],
  validatedData: { email: "invalid-email" },
  passedRules: 2,
  totalRules: 3
}`,
		},
		runtimeBehavior: 'Synchronous - validates data and routes based on results',
		errorsFailures: [
			'Invalid validation rule configuration',
			'Missing referenced fields',
			'Rule execution errors',
		],
		example: {
			title: 'Email Validation',
			description: 'Validate user email format and domain before sending',
			config: {
				rules: [
					{ field: 'email', type: 'isEmail' },
					{ field: 'email', type: 'regex', value: '@company\\.com$' },
				],
				onFail: 'stop',
			},
		},
	},
	{
		id: 'storage',
		name: 'Storage Node',
		icon: '💾',
		category: 'advanced',
		description: 'Stores key-value data in external storage with optional TTL',
		whenToUse: [
			'Cache API responses',
			'Store temporary workflow state',
			'Share data between workflow runs',
			'Implement rate limiting counters',
		],
		configurationOptions: [
			{
				name: 'key',
				type: 'string',
				example: 'user_{{userId}}_data',
				description: 'Storage key with token support',
				required: true,
			},
			{
				name: 'value',
				type: 'string',
				example: '{{api.response}}',
				description: 'Data to store (supports tokens)',
				required: true,
			},
			{
				name: 'ttl',
				type: 'number',
				example: '3600',
				description: 'Time to live in seconds (optional)',
			},
		],
		inputs: ['Data to store and key parameters from upstream nodes'],
		outputs: {
			description: 'Storage operation results',
			structure: `context.data.storage = {
  key: "user_123_data",
  stored: true,
  storedAt: "2024-01-15T10:00:00Z",
  expiresAt: "2024-01-15T11:00:00Z"
}`,
		},
		runtimeBehavior: 'Asynchronous - stores data and waits for confirmation',
		errorsFailures: [
			'Storage service unavailable',
			'Key formatting errors',
			'Data serialization failures',
			'Storage quota exceeded',
		],
		example: {
			title: 'API Response Caching',
			description: 'Cache user profile data for 1 hour to reduce API calls',
			config: {
				key: 'user_profile_{{userId}}',
				value: '{{api.response.user}}',
				ttl: 3600,
			},
		},
	},
	{
		id: 'webhook-caller',
		name: 'Webhook Caller Node',
		icon: '📡',
		category: 'advanced',
		description: 'Sends HTTP webhooks to external systems with workflow data',
		whenToUse: [
			'Notify external systems of events',
			'Trigger other workflows',
			'Send data to analytics platforms',
			'Integration with webhook-based services',
		],
		configurationOptions: [
			{
				name: 'url',
				type: 'string',
				example: 'https://hooks.example.com/webhook',
				description: 'Webhook URL',
				required: true,
			},
			{
				name: 'method',
				type: 'select',
				example: 'POST',
				description: 'HTTP method',
				required: true,
			},
			{
				name: 'headers',
				type: 'object',
				example: '{"Authorization": "Bearer {{token}}"}',
				description: 'HTTP headers',
			},
			{
				name: 'body',
				type: 'text',
				example: '{"event": "user_registered", "user": {{user}}}',
				description: 'Request body with tokens',
			},
			{
				name: 'async',
				type: 'boolean',
				example: 'true',
				description: 'Fire-and-forget vs wait for response',
			},
		],
		inputs: ['Workflow data to include in webhook payload'],
		outputs: {
			description: 'Webhook sending results (varies by async setting)',
			structure: `context.data.webhook = {
  sent: true,
  response: { status: "received" },
  statusCode: 200,
  responseTime: 150
}`,
		},
		runtimeBehavior: 'Configurable - synchronous or asynchronous based on async setting',
		errorsFailures: [
			'Network connectivity issues',
			'Invalid webhook URL',
			'HTTP error responses',
			'Request timeout',
		],
		example: {
			title: 'Analytics Event',
			description: 'Send user registration event to analytics platform',
			config: {
				url: 'https://analytics.example.com/events',
				method: 'POST',
				body: '{"event": "user_registered", "userId": "{{userId}}", "timestamp": "{{now}}"}',
				async: true,
			},
		},
	},
	{
		id: 'switch',
		name: 'Switch Node',
		icon: '🔀',
		category: 'advanced',
		description: 'Multi-branch conditional routing based on expression evaluation',
		whenToUse: [
			'Route based on multiple conditions',
			'Implement complex business logic',
			'Handle different user types',
			'Process various data formats',
		],
		configurationOptions: [
			{
				name: 'cases',
				type: 'array',
				example:
					'[{"expression": "{{userType}} === \'premium\'", "label": "Premium Users"}]',
				description: 'Case expressions and labels',
				required: true,
			},
			{
				name: 'defaultPath',
				type: 'string',
				example: 'standard',
				description: 'Default route if no cases match',
			},
		],
		inputs: ['Data to evaluate in case expressions'],
		outputs: {
			description: 'Switch routing results',
			structure: `context.data.switch = {
  matchedCase: "Premium Users",
  caseIndex: 0,
  usedDefault: false,
  evaluatedData: { userType: "premium" }
}`,
		},
		runtimeBehavior: 'Synchronous - evaluates cases and routes to matching branch',
		errorsFailures: [
			'Invalid expression syntax',
			'Missing referenced fields',
			'Expression evaluation errors',
		],
		example: {
			title: 'User Type Routing',
			description: 'Route users to different processes based on subscription type',
			config: {
				cases: [
					{ expression: '{{userType}} === "premium"', label: 'Premium Users' },
					{ expression: '{{userType}} === "trial"', label: 'Trial Users' },
				],
				defaultPath: 'Standard Users',
			},
		},
	},
	{
		id: 'retry',
		name: 'Retry Node',
		icon: '↻',
		category: 'advanced',
		description: 'Wraps child nodes and retries on failure with configurable strategy',
		whenToUse: [
			'Handle transient API failures',
			'Retry database connections',
			'Improve workflow reliability',
			'Handle rate-limited services',
		],
		configurationOptions: [
			{
				name: 'retries',
				type: 'number',
				example: '3',
				description: 'Maximum number of retry attempts',
				required: true,
			},
			{
				name: 'delayMs',
				type: 'number',
				example: '1000',
				description: 'Base delay between retries in ms',
				required: true,
			},
			{
				name: 'strategy',
				type: 'select',
				example: 'exponential',
				description: 'Retry strategy (fixed, exponential)',
				required: true,
			},
			{
				name: 'maxDelayMs',
				type: 'number',
				example: '10000',
				description: 'Maximum delay for exponential backoff',
			},
		],
		inputs: ['Input for child nodes that may fail and need retrying'],
		outputs: {
			description: 'Retry execution results and final outcome',
			structure: `context.data.retry = {
  attempts: 2,
  succeeded: true,
  lastError: null,
  totalRetryTime: 3000,
  result: { success: true, data: "..." }
}`,
		},
		runtimeBehavior: 'Asynchronous - retries child node execution with delays between attempts',
		errorsFailures: [
			'All retry attempts exhausted',
			'Invalid retry configuration',
			'Child node permanent failures',
		],
		example: {
			title: 'API Call Retry',
			description: 'Retry API calls up to 3 times with exponential backoff',
			config: {
				retries: 3,
				delayMs: 1000,
				strategy: 'exponential',
				maxDelayMs: 10000,
			},
		},
	},
	{
		id: 'parallel',
		name: 'Parallel Node',
		icon: '📚',
		category: 'advanced',
		description: 'Executes multiple workflow branches concurrently and merges results',
		whenToUse: [
			'Execute independent API calls simultaneously',
			'Parallel data processing',
			'Concurrent validation checks',
			'Optimize workflow performance',
		],
		configurationOptions: [
			{
				name: 'branches',
				type: 'array',
				example: '[{"label": "API Call", "description": "Fetch user data"}]',
				description: 'Branch definitions',
				required: true,
			},
			{
				name: 'waitForAll',
				type: 'boolean',
				example: 'true',
				description: 'Wait for all branches vs continue when any completes',
			},
		],
		inputs: ['Input data available to all parallel branches'],
		outputs: {
			description: 'Combined results from all parallel branches',
			structure: `context.data.parallel = {
  branch_0: { apiResult: "..." },
  branch_1: { dbResult: "..." },
  completedBranches: 2,
  allCompleted: true
}`,
		},
		runtimeBehavior:
			'Asynchronous - executes branches in parallel, waits based on waitForAll setting',
		errorsFailures: [
			'Branch execution failures',
			'Resource contention',
			'Partial completion with waitForAll=false',
		],
		example: {
			title: 'Concurrent Data Fetch',
			description: 'Fetch user profile and preferences simultaneously',
			config: {
				branches: [
					{ label: 'User Profile', description: 'Fetch from users API' },
					{ label: 'User Preferences', description: 'Fetch from preferences API' },
				],
				waitForAll: true,
			},
		},
	},
	{
		id: 'webhook-listener',
		name: 'Webhook Listener Node',
		icon: '📨',
		category: 'advanced',
		description: 'Pauses workflow until specific webhook is received',
		whenToUse: [
			'Wait for external system notifications',
			'Implement async integrations',
			'Handle payment confirmations',
			'Process third-party callbacks',
		],
		configurationOptions: [
			{
				name: 'expectedFields',
				type: 'array',
				example: '["orderId", "status"]',
				description: 'Required fields in webhook payload',
				required: true,
			},
			{
				name: 'secureToken',
				type: 'string',
				example: '{{webhook_secret}}',
				description: 'Token for webhook verification',
			},
			{
				name: 'timeoutMs',
				type: 'number',
				example: '300000',
				description: 'Timeout waiting for webhook (ms)',
			},
		],
		inputs: ['Context data for webhook correlation (e.g., order ID)'],
		outputs: {
			description: 'Webhook payload and metadata',
			structure: `context.data.webhook = {
  webhook: { orderId: "123", status: "completed" },
  receivedAt: "2024-01-15T10:00:00Z",
  headers: { "x-signature": "..." },
  verified: true
}`,
		},
		runtimeBehavior: 'Interactive - pauses workflow until matching webhook is received',
		errorsFailures: [
			'Webhook timeout',
			'Missing expected fields',
			'Signature verification failure',
			'Webhook endpoint unreachable',
		],
		example: {
			title: 'Payment Confirmation',
			description: 'Wait for payment processor webhook before proceeding',
			config: {
				expectedFields: ['paymentId', 'status', 'amount'],
				secureToken: '{{payment_webhook_secret}}',
				timeoutMs: 300000,
			},
		},
	},
];

export const HELP_CATEGORIES = [
	{ id: 'triggers', name: 'Triggers', color: 'green' },
	{ id: 'flow-control', name: 'Flow Control', color: 'emerald' },
	{ id: 'processing', name: 'Processing', color: 'blue' },
	{ id: 'integrations', name: 'Integrations', color: 'purple' },
	{ id: 'advanced', name: 'Advanced', color: 'orange' },
] as const;
